import moment from "moment";
import SingleSelectComponent from "../../components/single-select/single-select.component";
import { formats } from "../../config/constants";
import { transStatus } from "../../config/global-variables";
import languageModel from "../../models/language.model";
import IncentiveSchemeVariableModel from "../../models/incentive-scheme-variable/incentive-scheme-variable.model";
import ViewBase from "../view-base.view";
import IncentiveSchemeEditDialog from "./incentive-scheme-edit.dialog";
import IncentiveSchemeCloneDialog from "./incentive-scheme-clone.dialog";

/**
 * Incentive Scheme Variable Management
 *
 * FFIS scheme configuration: pick a scheme, then edit its pipeline through tabbed
 * parameter groups. A flat grid of ~120 parameters is unusable, hence the tabs.
 *
 * Two behaviours here matter more than the CRUD:
 *   - Versioning, not editing. Activating supersedes the previous version and never
 *     mutates history, so an ACTIVE scheme that already produced results should be
 *     cloned to a new version rather than edited.
 *   - The set-replace semantics. Parameters, ladders, caps, grades and rate matrices
 *     are saved as WHOLE SETS: the stored procedure deletes what the payload omits.
 *     Every save here therefore posts the full table, never the edited row alone.
 *
 * @author FFIS
 */
export default function IncentiveSchemeVariableView() {
  ViewBase.call(this);

  const PRODUCT_GROUPS = [
    { key: 'MX', value: 'MX' },
    { key: 'AV', value: 'AV' },
  ];

  const STATUS_CODES = [
    { key: 'DRAFT', value: 'DRAFT' },
    { key: 'ACTIVE', value: 'ACTIVE' },
    { key: 'CLOSED', value: 'CLOSED' },
  ];

  const TABS = {
    scheme: 'SCHEME',
    components: 'COMPONENTS',
    matrix: 'MATRIX',
    ladders: 'LADDERS',
    caps: 'CAPS',
    grades: 'GRADES',
    parameters: 'PARAMETERS',
    disqualification: 'DISQUALIFICATION',
    audit: 'AUDIT',
  };

  const DEFAULT_FILTERS = {
    productGroupCode: '',
    division: '',
    statusCode: '',
    asOfDate: '',
  };

  let filters = structuredClone(DEFAULT_FILTERS);

  let schemes = [];
  let calculators = [];

  let selectedSchemeId = null;
  let detail = null;
  let activeTab = TABS.scheme;

  /** Which component's matrix the Rates tab is showing. */
  let matrixComponentCode = '';

  let isEditOpen = false;
  let isCloneOpen = false;
  let editAction = '';

  /* ---- loading ---------------------------------------------------------- */

  const searchSchemes = () => {
    this.showLoading();
    IncentiveSchemeVariableModel.searchSchemes(filters)
      .then((result) => {
        schemes = result || [];
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  const getCalculators = () => {
    IncentiveSchemeVariableModel.getCalculators()
      .then((result) => {
        calculators = (result || []).map(c => ({
          key: c.calculatorCode,
          value: `${c.calculatorCode} - ${c.calculatorName}`,
        }));
      })
      .catch(() => {
        // A failed picker must not block the page; the scheme form validates the
        // code server-side anyway.
        calculators = [];
      });
  };

  const loadDetail = (schemeId) => {
    this.showLoading();
    IncentiveSchemeVariableModel.getSchemeDetail(schemeId)
      .then((result) => {
        detail = result;
        selectedSchemeId = schemeId;

        // Default the Rates tab to the first component that actually has cells,
        // so the tab is not empty on arrival for no reason.
        const firstWithCells = (detail?.rateMatrix || [])[0];
        matrixComponentCode = firstWithCells
          ? firstWithCells.componentCode
          : ((detail?.components || [])[0]?.componentCode || '');
      })
      .catch((error) => {
        detail = null;
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  const clearFilter = () => {
    filters = structuredClone(DEFAULT_FILTERS);
    schemes = [];
    detail = null;
    selectedSchemeId = null;
  };

  /* ---- saving ----------------------------------------------------------- */

  /**
   * Every set-replace save funnels through here so the envelope is read the same
   * way once, and so a save always reloads: the procedures normalise and stamp
   * audit columns, and showing stale client state after a write is how a config
   * page starts lying about what is stored.
   */
  const saveSet = (promise) => {
    this.showLoading();
    promise
      .then((result) => {
        const trans = Array.isArray(result) ? result[0] : result?.transResult?.[0];
        this.showMessage(
          this.getText(trans?.code, trans?.message),
          trans?.transStatus,
          () => {
            if (trans?.transStatus === transStatus.success) {
              loadDetail(selectedSchemeId);
            }
          });
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  const onActivate = () => {
    this.showConfirm(
      this.getText('EM00000266',
        'Activating supersedes the previous version of this scheme. Continue?'),
      this.getText('LT00000022', 'Save'),
      (e) => {
        if (e === 'YES') {
          saveSet(IncentiveSchemeVariableModel.activateScheme(selectedSchemeId));
        }
      });
  };

  const saveParameters = () => saveSet(
    IncentiveSchemeVariableModel.saveParameters(selectedSchemeId, detail.parameters));

  const saveCaps = () => saveSet(
    IncentiveSchemeVariableModel.saveCaps(selectedSchemeId, detail.caps));

  const saveGrades = () => saveSet(
    IncentiveSchemeVariableModel.saveGrades(selectedSchemeId, detail.grades));

  /**
   * Ladders are keyed by (componentCode, ladderCode) and each pair is replaced
   * independently, so the rows are grouped and posted one pair at a time. Posting
   * them all in one call would make the last pair delete the others.
   */
  const saveLadders = () => {
    const groups = {};
    (detail.arLadders || []).forEach((row) => {
      const key = `${row.componentCode}||${row.ladderCode}`;
      groups[key] = groups[key] || [];
      groups[key].push(row);
    });

    const keys = Object.keys(groups);
    if (!keys.length) {
      return;
    }

    this.showLoading();
    Promise.all(keys.map((key) => {
      const [componentCode, ladderCode] = key.split('||');
      return IncentiveSchemeVariableModel.saveArLadder(
        selectedSchemeId, componentCode, ladderCode, groups[key]);
    }))
      .then(() => {
        this.showMessage(
          this.getText('EM00000004', 'Saved successfully.'),
          transStatus.success,
          () => { loadDetail(selectedSchemeId); });
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  const saveMatrix = () => {
    const rows = (detail.rateMatrix || [])
      .filter(r => r.componentCode === matrixComponentCode);

    if (!rows.length) {
      return;
    }

    // MatrixCode is part of the replace key. SEIN MX stores the weekly and monthly
    // rate blocks as two separate matrices rather than one rate split 60/40, so
    // each block is saved on its own.
    const byMatrixCode = {};
    rows.forEach((row) => {
      byMatrixCode[row.matrixCode] = byMatrixCode[row.matrixCode] || [];
      byMatrixCode[row.matrixCode].push(row);
    });

    this.showLoading();
    Promise.all(Object.keys(byMatrixCode).map(matrixCode =>
      IncentiveSchemeVariableModel.saveRateMatrix(
        selectedSchemeId, matrixComponentCode, matrixCode, byMatrixCode[matrixCode])))
      .then(() => {
        this.showMessage(
          this.getText('EM00000004', 'Saved successfully.'),
          transStatus.success,
          () => { loadDetail(selectedSchemeId); });
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  /* ---- small render helpers --------------------------------------------- */

  const noRows = (colspan) => m('tr.align-middle',
    m('td.text-center', { colspan: String(colspan) },
      languageModel.getText('EM00000141', 'No records found.')));

  /** Text cell bound to a row property. */
  const editText = (row, prop) => m('input.form-control.form-control-sm', {
    value: row[prop] ?? '',
    onchange: (e) => { row[prop] = e.target.value.trim(); },
  });

  /** Numeric cell. Empty string is preserved as null, because 0 and "no value" are
      different things here - a cap of zero is not the absence of a cap. */
  const editNumber = (row, prop, step) => m('input.form-control.form-control-sm', {
    type: 'number',
    step: step || 'any',
    value: row[prop] ?? '',
    onchange: (e) => {
      const raw = e.target.value;
      row[prop] = raw === '' ? null : Number(raw);
    },
  });

  const editCheck = (row, prop) => m('input.form-check-input', {
    type: 'checkbox',
    checked: !!row[prop],
    onchange: (e) => { row[prop] = e.target.checked; },
  });

  const saveBar = (onSave, note) => m('.col-12.d-flex.justify-content-end.align-items-center.gap-2.mt-2',
    note && m('small.text-muted.me-auto', note),
    m('button.btn.btn-primary', { onclick: onSave }, this.getText('LT00000022', 'Save')));

  const statusBadge = (code) => {
    const cls = code === 'ACTIVE' ? 'bg-success'
      : code === 'CLOSED' ? 'bg-secondary'
        : 'bg-warning text-dark';
    return m(`span.badge.${cls.split(' ').join('.')}`, code || '-');
  };

  /* ---- tabs ------------------------------------------------------------- */

  const schemeTab = () => {
    const s = detail.scheme;
    return m('.row.g-3',
      m('.col-md-4', m('label.form-label.text-muted', this.getText('LT00000576', 'Scheme Code')),
        m('div', m('strong', s.schemeCode))),
      m('.col-md-4', m('label.form-label.text-muted', this.getText('LT00000002', 'Name')),
        m('div', s.schemeName)),
      m('.col-md-2', m('label.form-label.text-muted', this.getText('LT00000003', 'Version')),
        m('div', s.schemeVersion)),
      m('.col-md-2', m('label.form-label.text-muted', this.getText('LT00000004', 'Status')),
        m('div', statusBadge(s.statusCode))),

      m('.col-md-4', m('label.form-label.text-muted', this.getText('LT00000005', 'Calculator')),
        m('div', `${s.calculatorCode || '-'}${s.calculatorName ? ' - ' + s.calculatorName : ''}`)),
      m('.col-md-2', m('label.form-label.text-muted', this.getText('LT00000006', 'Currency')),
        m('div', s.currency)),
      m('.col-md-2', m('label.form-label.text-muted', this.getText('LT00000008', 'Product Group')),
        m('div', s.productGroupCode)),
      m('.col-md-4', m('label.form-label.text-muted', this.getText('LT00000009', 'Division')),
        m('div', s.division || '-')),

      m('.col-md-3', m('label.form-label.text-muted', this.getText('LT00000010', 'Effective From')),
        m('div', s.periodFrom)),
      m('.col-md-3', m('label.form-label.text-muted', this.getText('LT00000011', 'Effective To')),
        m('div', s.periodTo || '-')),
      m('.col-md-3', m('label.form-label.text-muted', this.getText('LT00000012', 'Rounding')),
        m('div', `${s.roundingRule || '-'} / ${s.roundingStage || '-'}`)),
      m('.col-md-3', m('label.form-label.text-muted', this.getText('LT00000013', 'Supersedes')),
        m('div', s.supersedesSchemeId || '-')),

      m('.col-12', m('label.form-label.text-muted', this.getText('LT00000014', 'Remark')),
        m('div', s.remark || '-')),

      m('.col-12.border-top.pt-2',
        m('small.text-muted',
          `${this.getText('LT00000015', 'Created')}: ${s.createUserId || '-'}`
          + ` ${s.createDate ? moment(s.createDate).format(formats.LOCAL_TIME) : ''}`
          + `   |   ${this.getText('LT00000016', 'Updated')}: ${s.updateUserId || '-'}`
          + ` ${s.updateDate ? moment(s.updateDate).format(formats.LOCAL_TIME) : ''}`
          + (s.approvedBy ? `   |   ${this.getText('LT00000017', 'Approved')}: ${s.approvedBy}` : ''))));
  };

  const componentsTab = () => m('.table-responsive',
    m('table.table.table-hover.table-sm',
      m('thead', m('tr.align-middle',
        m('th', this.getText('LT00000018', 'Seq')),
        m('th', this.getText('LT00000019', 'Component')),
        m('th', this.getText('LT00000002', 'Name')),
        m('th', this.getText('LT00000020', 'Type')),
        m('th', this.getText('LT00000023', 'Position Tier')),
        m('th', this.getText('LT00000024', 'Enabled')),
        m('th', this.getText('LT00000025', 'Always On')),
        m('th', this.getText('LT00000026', 'Floor')),
        m('th', this.getText('LT00000027', 'Over-Ach. Cap')),
        m('th', this.getText('LT00000028', 'Inside Cap')),
        m('th', this.getText('LT00000029', 'Wk/Mth Split')))),
      m('tbody',
        !(detail.components || []).length && noRows(11),
        (detail.components || []).map(c => m('tr.align-middle',
          m('td', c.sequence),
          m('td', m('span.cursor-text', c.componentCode)),
          m('td', c.componentName),
          m('td', c.componentType),
          m('td', c.positionTier || c.positionCode || '-'),
          m('td', c.enabled ? m('i.bi.bi-check-lg.text-success') : m('i.bi.bi-dash')),
          m('td', c.alwaysOn ? m('i.bi.bi-check-lg.text-success') : m('i.bi.bi-dash')),
          m('td', c.floorGate ?? '-'),
          m('td', c.overAchievementCap ?? '-'),
          // Inside/outside the cap is not cosmetic: for EC the campaign booster is
          // inside, for the eight non-EC roles it is outside.
          m('td', c.insideCap
            ? m('span.badge.bg-info.text-dark', this.getText('LT00000030', 'Inside'))
            : m('span.badge.bg-light.text-dark', this.getText('LT00000032', 'Outside'))),
          m('td', c.weeklyMonthlySplit ?? '-'))))));

  const matrixTab = () => {
    const components = [...new Set((detail.components || []).map(c => c.componentCode))]
      .map(code => ({ key: code, value: code }));
    const rows = (detail.rateMatrix || [])
      .filter(r => r.componentCode === matrixComponentCode);

    return m('.row.g-2',
      m('.col-md-4',
        m('label.form-label', this.getText('LT00000019', 'Component')),
        m(SingleSelectComponent, {
          id: 'matrixComponentSelect',
          items: components,
          selectedItem: matrixComponentCode,
          keyOnly: true,
          filterable: true,
          onSelect: (item) => { matrixComponentCode = item; },
        })),
      m('.col-12.table-responsive',
        m('table.table.table-hover.table-sm',
          m('thead', m('tr.align-middle',
            m('th', this.getText('LT00000033', 'Matrix')),
            m('th', this.getText('LT00000034', 'Axis 1')),
            m('th', this.getText('LT00000035', 'Axis 1 Code')),
            m('th', this.getText('LT00000036', 'Axis 2')),
            m('th', this.getText('LT00000037', 'Axis 2 Code')),
            m('th', this.getText('LT00000038', 'Rate')),
            m('th', this.getText('LT00000039', 'Amount')),
            m('th', this.getText('LT00000040', 'Multiplier')))),
          m('tbody',
            !rows.length && noRows(8),
            rows.map(r => m('tr.align-middle',
              m('td', m('span.cursor-text', r.matrixCode)),
              m('td', r.axis1Type || '-'),
              m('td', r.axis1Code || '-'),
              m('td', r.axis2Type || '-'),
              m('td', r.axis2Code || '-'),
              m('td', editNumber(r, 'rateValue', '0.000001')),
              m('td', editNumber(r, 'amountValue', '0.0001')),
              m('td', editNumber(r, 'multiplierValue', '0.000001'))))))),
      rows.length && saveBar(saveMatrix,
        this.getText('EM00000267',
          'Saving replaces every cell of this component’s matrix.')));
  };

  const laddersTab = () => m('.row.g-2',
    m('.col-12.table-responsive',
      m('table.table.table-hover.table-sm',
        m('thead', m('tr.align-middle',
          m('th', this.getText('LT00000019', 'Component')),
          m('th', this.getText('LT00000041', 'Ladder')),
          m('th', this.getText('LT00000018', 'Seq')),
          m('th', this.getText('LT00000042', 'Tier')),
          m('th', this.getText('LT00000043', 'Label')),
          m('th', this.getText('LT00000044', 'From')),
          m('th', this.getText('LT00000045', 'To')),
          m('th', this.getText('LT00000040', 'Multiplier')),
          m('th', this.getText('LT00000046', 'Payout')))),
        m('tbody',
          !(detail.arLadders || []).length && noRows(9),
          (detail.arLadders || []).map(l => m('tr.align-middle',
            m('td', m('span.cursor-text', l.componentCode)),
            m('td', m('span.cursor-text', l.ladderCode)),
            m('td', l.sequence),
            m('td', l.tierCode),
            m('td', editText(l, 'tierLabel')),
            m('td', editNumber(l, 'thresholdFrom', '0.000001')),
            m('td', editNumber(l, 'thresholdTo', '0.000001')),
            m('td', editNumber(l, 'multiplierValue', '0.000001')),
            m('td', editNumber(l, 'payoutAmount', '0.0001'))))))),
    (detail.arLadders || []).length && saveBar(saveLadders,
      // Thresholds are fractions, not percentages, and that trips people up:
      // 0.90 is 90%.
      this.getText('EM00000268',
        'Thresholds are fractions: 0.90 means 90%. Each component/ladder pair is replaced in full.')));

  const capsTab = () => m('.row.g-2',
    m('.col-12.table-responsive',
      m('table.table.table-hover.table-sm',
        m('thead', m('tr.align-middle',
          m('th', this.getText('LT00000047', 'Position')),
          m('th', this.getText('LT00000048', 'Store Class')),
          m('th', this.getText('LT00000049', 'Rule')),
          m('th', this.getText('LT00000050', 'Cap Amount')),
          m('th', this.getText('LT00000051', 'Applied')),
          m('th', this.getText('LT00000014', 'Remark')))),
        m('tbody',
          !(detail.caps || []).length && noRows(6),
          (detail.caps || []).map(c => m('tr.align-middle',
            m('td', m('span.cursor-text', c.positionCode)),
            m('td', c.storeClassCode || '-'),
            m('td', editText(c, 'storeClassRule')),
            m('td', editNumber(c, 'capAmount', '0.0001')),
            // How many times the cap is applied in this role's pipeline - 3 for EC.
            m('td', editNumber(c, 'capApplicationCount', '1')),
            m('td', editText(c, 'remark'))))))),
    (detail.caps || []).length && saveBar(saveCaps));

  const gradesTab = () => m('.row.g-2',
    m('.col-12.table-responsive',
      m('table.table.table-hover.table-sm',
        m('thead', m('tr.align-middle',
          m('th', this.getText('LT00000018', 'Seq')),
          m('th', this.getText('LT00000052', 'Grade')),
          m('th', this.getText('LT00000043', 'Label')),
          m('th', this.getText('LT00000047', 'Position')),
          m('th', this.getText('LT00000053', 'Location')),
          m('th', this.getText('LT00000054', 'Effect')),
          m('th', this.getText('LT00000055', 'Delta')),
          m('th', this.getText('LT00000056', 'Pot')))),
        m('tbody',
          !(detail.grades || []).length && noRows(8),
          (detail.grades || []).map(g => m('tr.align-middle',
            m('td', g.sequence),
            m('td', m('span.cursor-text', g.gradeCode)),
            m('td', editText(g, 'gradeLabel')),
            m('td', g.positionCode || '-'),
            m('td', g.locationClass || '-'),
            m('td', g.effectType),
            m('td', editNumber(g, 'deltaValue', '0.000001')),
            m('td', editNumber(g, 'potAmount', '0.0001'))))))),
    (detail.grades || []).length && saveBar(saveGrades,
      // DELTA and MULTIPLIER are not interchangeable - the source workbooks express
      // the same grading two different ways and conflating them changes pay.
      this.getText('EM00000269',
        'A DELTA is added to Incentive Regular Total; a MULTIPLIER scales it. Check Effect before editing.')));

  const parametersTab = () => m('.row.g-2',
    m('.col-12.table-responsive',
      m('table.table.table-hover.table-sm',
        m('thead', m('tr.align-middle',
          m('th', this.getText('LT00000019', 'Component')),
          m('th', this.getText('LT00000057', 'Parameter')),
          m('th', this.getText('LT00000020', 'Type')),
          m('th', this.getText('LT00000059', 'Value')),
          m('th', this.getText('LT00000060', 'Text')),
          m('th', this.getText('LT00000061', 'UoM')),
          m('th', this.getText('LT00000024', 'Active')),
          m('th', this.getText('LT00000014', 'Remark')))),
        m('tbody',
          !(detail.parameters || []).length && noRows(8),
          (detail.parameters || []).map(p => m('tr.align-middle',
            { class: p.active ? '' : 'table-warning' },
            m('td', p.componentCode || '-'),
            m('td', m('span.cursor-text', p.paramCode)),
            m('td', p.paramType),
            m('td', editNumber(p, 'paramValueNum', '0.000001')),
            m('td', editText(p, 'paramValueText')),
            m('td', p.uom || '-'),
            m('td', editCheck(p, 'active')),
            m('td', m('small.text-muted', p.remark || ''))))))),
    (detail.parameters || []).length && saveBar(saveParameters,
      // The inactive rows are inactive on purpose; each would change somebody's pay.
      this.getText('EM00000270',
        'Highlighted rows are INACTIVE pending a business decision. Switching one on changes pay.')));

  const disqualificationTab = () => m('.row.g-2',
    m('.col-12',
      m('.alert.alert-secondary.py-2.mb-2',
        m('small', this.getText('EM00000271',
          'Read-only: no save endpoint exists for disqualification rules yet. They are copied when a scheme is cloned.')))),
    m('.col-12.table-responsive',
      m('table.table.table-hover.table-sm',
        m('thead', m('tr.align-middle',
          m('th', this.getText('LT00000018', 'Seq')),
          m('th', this.getText('LT00000062', 'Rule')),
          m('th', this.getText('LT00000002', 'Name')),
          m('th', this.getText('LT00000063', 'Scope')),
          m('th', this.getText('LT00000024', 'Enabled')),
          m('th', this.getText('LT00000064', 'Source')))),
        m('tbody',
          !(detail.disqualificationRules || []).length && noRows(6),
          (detail.disqualificationRules || []).map(r => m('tr.align-middle',
            m('td', r.sequence),
            m('td', m('span.cursor-text', r.ruleCode)),
            m('td', r.ruleName,
              r.ruleDescription && m('div', m('small.text-muted', r.ruleDescription))),
            m('td', r.scopeType || '-'),
            m('td', r.enabled ? m('i.bi.bi-check-lg.text-success') : m('i.bi.bi-dash')),
            m('td', m('small.text-muted', r.policySourceRef || '-'))))))));

  let auditRows = [];
  const auditTab = () => m('.row.g-2',
    m('.col-12',
      m('.alert.alert-secondary.py-2.mb-2',
        m('small', this.getText('EM00000272',
          'Last change per row, not a change history: FFIS stores only create/update stamps, so a value edited three times appears once.')))),
    m('.col-12.table-responsive',
      m('table.table.table-hover.table-sm',
        m('thead', m('tr.align-middle',
          m('th', this.getText('LT00000065', 'Area')),
          m('th', this.getText('LT00000066', 'Record')),
          m('th', this.getText('LT00000067', 'Changed By')),
          m('th', this.getText('LT00000068', 'Changed')))),
        m('tbody',
          !auditRows.length && noRows(4),
          auditRows.map(a => m('tr.align-middle',
            m('td', m('span.badge.bg-light.text-dark', a.configArea)),
            m('td', m('span.cursor-text', a.recordLabel)),
            m('td', a.lastChangedBy || '-'),
            m('td', a.lastChangedDate
              ? moment(a.lastChangedDate).format(formats.LOCAL_TIME) : '-')))))));

  const loadAudit = () => {
    this.showLoading();
    IncentiveSchemeVariableModel.getAudit(selectedSchemeId)
      .then((result) => { auditRows = result || []; })
      .catch((error) => { this.handleError(error); })
      .finally(() => { this.hideLoading(); });
  };

  const onTabClick = (tab) => {
    activeTab = tab;
    if (tab === TABS.audit) {
      loadAudit();
    }
  };

  const TAB_LABELS = [
    [TABS.scheme, 'LT00000069', 'Scheme'],
    [TABS.components, 'LT00000071', 'Components'],
    [TABS.matrix, 'LT00000072', 'Rates & Matrices'],
    [TABS.ladders, 'LT00000073', 'AR Ladders'],
    [TABS.caps, 'LT00000074', 'Caps'],
    [TABS.grades, 'LT00000075', 'Grades'],
    [TABS.parameters, 'LT00000076', 'Parameters & Boosters'],
    [TABS.disqualification, 'LT00000077', 'Disqualification'],
    [TABS.audit, 'LT00000078', 'Audit'],
  ];

  const tabBody = () => {
    switch (activeTab) {
      case TABS.components: return componentsTab();
      case TABS.matrix: return matrixTab();
      case TABS.ladders: return laddersTab();
      case TABS.caps: return capsTab();
      case TABS.grades: return gradesTab();
      case TABS.parameters: return parametersTab();
      case TABS.disqualification: return disqualificationTab();
      case TABS.audit: return auditTab();
      default: return schemeTab();
    }
  };

  /* ---- view ------------------------------------------------------------- */

  return {
    oninit: () => {
      getCalculators();
      searchSchemes();
    },

    view: () => {
      return [
        m('.d-flex.justify-content-end.align-items-end.mb-3',
          m('button.btn.btn-outline-primary.btn-add', {
            title: this.getText('LT00000058', 'Create'),
            onclick: () => { editAction = 'INSERT'; isEditOpen = true; },
          }, m('i.bi.bi-plus-lg.me-0'))),

        /* SEARCH */
        m('.d-flex.flex-column.align-items-end.gap-2.mb-3',
          m('.card.collapse.show#search-section.w-100',
            m('.card-body',
              m('.row.g-4',
                m('.col-md-3.col-lg-3.position-relative.gap-2',
                  m('label.form-label[for=productGroupSelect]',
                    this.getText('LT00000008', 'Product Group')),
                  m(SingleSelectComponent, {
                    id: 'productGroupSelect',
                    items: PRODUCT_GROUPS,
                    selectedItem: filters.productGroupCode,
                    checkmark: true,
                    keyOnly: true,
                    onSelect: (item) => {
                      filters.productGroupCode = item;
                      getCalculators();
                    },
                  })),

                m('.col-md-3.col-lg-3.position-relative.gap-2',
                  m('label.form-label', this.getText('LT00000009', 'Division')),
                  m('input.form-control.form-control-bg', {
                    value: filters.division,
                    onchange: (e) => { filters.division = e.target.value.trim(); },
                  })),

                m('.col-md-3.col-lg-3.position-relative.gap-2',
                  m('label.form-label[for=statusSelect]',
                    this.getText('LT00000004', 'Status')),
                  m(SingleSelectComponent, {
                    id: 'statusSelect',
                    items: STATUS_CODES,
                    selectedItem: filters.statusCode,
                    checkmark: true,
                    keyOnly: true,
                    onSelect: (item) => { filters.statusCode = item; },
                  })),

                m('.col-md-3.col-lg-3.position-relative.gap-2',
                  m('label.form-label', this.getText('LT00000079', 'Effective On')),
                  m('input.form-control.form-control-bg', {
                    placeholder: 'YYYYMMDD',
                    maxlength: 8,
                    value: filters.asOfDate,
                    onchange: (e) => { filters.asOfDate = e.target.value.trim(); },
                  })),

                m('.col-12.d-flex.justify-content-end.gap-2',
                  m('button.btn.btn-primary', { onclick: searchSchemes },
                    this.getText('LT00000001', 'Search')),
                  m('button.btn.btn-white', { onclick: clearFilter },
                    this.getText('LT00000031', 'Clear'))))))),

        /* SCHEME LIST */
        m('.card.mb-3',
          m('.card-body',
            m('.table-responsive',
              m('table.table.table-hover.table-sm',
                m('thead', m('tr.align-middle',
                  m('th', this.getText('LT00000576', 'Scheme Code')),
                  m('th', this.getText('LT00000002', 'Name')),
                  m('th', this.getText('LT00000003', 'Version')),
                  m('th', this.getText('LT00000008', 'PG')),
                  m('th', this.getText('LT00000006', 'Currency')),
                  m('th', this.getText('LT00000010', 'From')),
                  m('th', this.getText('LT00000011', 'To')),
                  m('th', this.getText('LT00000004', 'Status')),
                  m('th', this.getText('LT00000092', 'Action')))),
                m('tbody',
                  !schemes.length && noRows(9),
                  schemes.map(s => m('tr.align-middle.cursor-pointer',
                    {
                      class: s.schemeId === selectedSchemeId ? 'table-primary' : '',
                      onclick: () => { loadDetail(s.schemeId); },
                    },
                    m('td', m('span.cursor-text', s.schemeCode)),
                    m('td', s.schemeName),
                    m('td', s.schemeVersion),
                    m('td', s.productGroupCode),
                    m('td', s.currency),
                    m('td', s.periodFrom),
                    m('td', s.periodTo || '-'),
                    m('td', statusBadge(s.statusCode)),
                    m('td',
                      m('i.bi.bi-pencil-square.list-action-icon[title="Edit"]', {
                        onclick: (e) => {
                          e.stopPropagation();
                          loadDetail(s.schemeId);
                          editAction = 'UPDATE';
                          isEditOpen = true;
                        },
                      }),
                      m('i.bi.bi-files.list-action-icon[title="Clone to new period"]', {
                        onclick: (e) => {
                          e.stopPropagation();
                          selectedSchemeId = s.schemeId;
                          isCloneOpen = true;
                        },
                      }))))))))),

        /* DETAIL */
        detail && detail.scheme && m('.card',
          m('.card-body',
            m('.d-flex.justify-content-between.align-items-center.mb-3',
              m('h5.mb-0',
                `${detail.scheme.schemeCode} `,
                m('small.text-muted', detail.scheme.schemeVersion)),
              m('div.d-flex.gap-2',
                detail.scheme.statusCode === 'DRAFT'
                && m('button.btn.btn-success.btn-sm', { onclick: onActivate },
                  this.getText('LT00000080', 'Activate')),
                m('button.btn.btn-outline-secondary.btn-sm', {
                  onclick: () => { isCloneOpen = true; },
                }, this.getText('LT00000081', 'Clone')))),

            m('ul.nav.nav-tabs.mb-3',
              TAB_LABELS.map(([tab, code, fallback]) =>
                m('li.nav-item',
                  m('a.nav-link.cursor-pointer',
                    {
                      class: activeTab === tab ? 'active' : '',
                      onclick: () => { onTabClick(tab); },
                    },
                    this.getText(code, fallback))))),

            tabBody())),

        isEditOpen && m(IncentiveSchemeEditDialog, {
          isOpen: isEditOpen,
          action: editAction,
          calculators: calculators,
          data: editAction === 'UPDATE' && detail ? detail.scheme : null,
          schemeId: editAction === 'UPDATE' ? selectedSchemeId : 0,
          closeDialog: (reload) => {
            isEditOpen = false;
            if (reload) {
              searchSchemes();
              if (selectedSchemeId) {
                loadDetail(selectedSchemeId);
              }
            }
          },
        }),

        isCloneOpen && m(IncentiveSchemeCloneDialog, {
          isOpen: isCloneOpen,
          schemeId: selectedSchemeId,
          closeDialog: (reload) => {
            isCloneOpen = false;
            if (reload) {
              searchSchemes();
            }
          },
        }),

        this.getBaseViews(),
      ];
    },
  };
}
